import React, { useState } from 'react';
import axios from 'axios';
import './AddressValidation.css';

const AddressValidation = ({ onBack }) => {
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleValidate = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        setResult(null);

        try {
            const apiUrl = process.env.NODE_ENV === 'development'
                ? 'http://localhost:5001/api/validate-address'
                : '/api/validate-address';

            const addressLines = [addressLine1];
            if (addressLine2) addressLines.push(addressLine2);

            const res = await axios.post(apiUrl, {
                addressLines,
                city,
                state,
                postalCode,
                countryCode: 'US'
            });

            if (res.data.success) {
                setResult(res.data);
            } else {
                setError(res.data.message || 'Validation failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error connecting to validation service');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="address-validation-container">
            <div className="address-validation-header">
                <button className="back-btn" onClick={onBack}>←</button>
                <h2>📍 UPS Address Validation</h2>
            </div>

            <div className="address-validation-content">
                <div className="validation-form-card">
                    <form onSubmit={handleValidate}>
                        <div className="form-group">
                            <label>Address Line 1</label>
                            <input
                                type="text"
                                placeholder="Street address, P.O. box, company name"
                                value={addressLine1}
                                onChange={(e) => setAddressLine1(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Address Line 2 (Optional)</label>
                            <input
                                type="text"
                                placeholder="Apartment, suite, unit, building, floor, etc."
                                value={addressLine2}
                                onChange={(e) => setAddressLine2(e.target.value)}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>City</label>
                                <input
                                    type="text"
                                    placeholder="e.g. New York"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>State</label>
                                <input
                                    type="text"
                                    placeholder="e.g. NY"
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                    required
                                    maxLength="2"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>ZIP Code</label>
                            <input
                                type="text"
                                placeholder="e.g. 10001"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                required
                            />
                        </div>

                        <button type="submit" className="validate-btn" disabled={loading}>
                            {loading ? 'Validating...' : 'Validate Address'}
                        </button>
                    </form>
                </div>

                <div className="validation-result-card">
                    {loading ? (
                        <div className="result-loading">
                            <div className="spinner"></div>
                            <p>Validating address with UPS database...</p>
                        </div>
                    ) : error ? (
                        <div className="result-error">
                            <div className="error-icon">❌</div>
                            <p>{error}</p>
                        </div>
                    ) : result ? (
                        <div className="result-data">
                            <div className={`status-banner ${result.isValid ? 'valid' : result.noCandidates ? 'invalid' : 'ambiguous'}`}>
                                <span className="status-icon">
                                    {result.isValid ? '✅' : result.noCandidates ? '❌' : '⚠️'}
                                </span>
                                <div className="status-text">
                                    <h3>
                                        {result.isValid ? 'Valid Address' :
                                            result.noCandidates ? 'Invalid Address' :
                                                'Ambiguous Address'}
                                    </h3>
                                    <p>
                                        {result.isValid ? 'Address verified with USPS database' :
                                            result.noCandidates ? 'No matching address found' :
                                                'Multiple possible matches found'}
                                    </p>
                                </div>
                            </div>

                            {result.classification && (
                                <div className="classification-box">
                                    <h4>Address Classification</h4>
                                    <p><strong>Type:</strong> {result.classification.Description || result.classification.Code}</p>
                                </div>
                            )}

                            {result.candidates && result.candidates.length > 0 && (
                                <div className="candidates-section">
                                    <h4>
                                        {result.candidates.length} Suggested Address{result.candidates.length > 1 ? 'es' : ''} Found:
                                    </h4>
                                    {console.log('Candidates:', result.candidates)}
                                    {result.candidates.map((candidate, index) => (
                                        <div key={index} className="candidate-card">
                                            <div className="candidate-header">
                                                <span className="candidate-number">#{index + 1}</span>
                                                {candidate.AddressClassification && (
                                                    <span className="candidate-type">
                                                        {candidate.AddressClassification.Description}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="candidate-address">
                                                {candidate.AddressKeyFormat.AddressLine?.map((line, i) => (
                                                    <div key={i}>{line}</div>
                                                ))}
                                                <div>
                                                    {candidate.AddressKeyFormat.PoliticalDivision2}, {candidate.AddressKeyFormat.PoliticalDivision1} {candidate.AddressKeyFormat.PostcodePrimaryLow}
                                                    {candidate.AddressKeyFormat.PostcodeExtendedLow && `-${candidate.AddressKeyFormat.PostcodeExtendedLow}`}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {result.alerts && result.alerts.length > 0 && (
                                <div className="result-alerts">
                                    <h4>System Alerts:</h4>
                                    {result.alerts.map((alert, i) => (
                                        <p key={i}>• {alert.Description || alert.description}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="result-placeholder">
                            <div className="placeholder-icon">📍</div>
                            <p>Enter a U.S. address to validate it against the USPS database. This service checks addresses in the United States and Puerto Rico.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddressValidation;
